// auszeichnung.ts — ein sehr kleines Stueck Markdown fuer Textfelder.
//
// Die Bausteine bekommen ihre Daten als Rohtext. Ohne jede Auszeichnung waere
// eine Tabellenzelle immer gleich schwer, und in Marianns Vorlagen ist genau das
// nicht so: dort steht Wichtiges fett und hinter Zahlen haengen Quellenlinks.
// Weil ihre Entwuerfe ohnehin als Markdown entstehen, versteht dieser Baustein
// dieselben drei Zeichen — mehr nicht.
//
//   **fett**            -> <strong>
//   *kursiv*            -> <em>
//   [Text](/adresse)    -> <a>
//
// Bewusst KEIN allgemeines HTML: Rohtext aus einem Designer-Feld ungeprueft als
// HTML einzusetzen waere eine offene Tuer. Hier entstehen nur echte Elemente und
// Textknoten, alles andere bleibt Text.
import { el, fragment, type Kind } from './dom';

/**
 * Laesst nur Ziele durch, die aus einem Textfeld sinnvoll sind. Alles andere —
 * allen voran `javascript:` — wird zu schlichtem Text, nicht zu einem Link.
 */
function istErlaubtesZiel(ziel: string): boolean {
  const sauber = ziel.trim().toLowerCase();
  if (sauber.startsWith('/') || sauber.startsWith('#')) return true;
  return (
    sauber.startsWith('https://') ||
    sauber.startsWith('http://') ||
    sauber.startsWith('mailto:') ||
    sauber.startsWith('tel:')
  );
}

/** Reihenfolge zaehlt: der Link muss vor fett/kursiv greifen. */
const MUSTER = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

/**
 * Wandelt einen Textausschnitt in Knoten. Ohne Treffer kommt ein schlichter
 * Textknoten zurueck, dann kostet der Aufruf praktisch nichts.
 */
export function zeichneAus(text: string): Node {
  if (!text) return document.createTextNode('');
  if (!text.includes('*') && !text.includes('[')) return document.createTextNode(text);

  const knoten: Kind[] = [];
  let zuletzt = 0;
  let treffer: RegExpExecArray | null;

  MUSTER.lastIndex = 0;
  while ((treffer = MUSTER.exec(text)) !== null) {
    if (treffer.index > zuletzt) knoten.push(text.slice(zuletzt, treffer.index));

    const [ganz, linkText, linkZiel, fett, kursiv] = treffer;

    if (linkText && linkZiel) {
      const extern = linkZiel.startsWith('http');
      knoten.push(
        istErlaubtesZiel(linkZiel)
          ? el(
              'a',
              {
                href: linkZiel,
                target: extern ? '_blank' : null,
                rel: extern ? 'noopener noreferrer' : null,
              },
              linkText,
            )
          : linkText,
      );
    } else if (fett) {
      knoten.push(el('strong', null, fett));
    } else if (kursiv) {
      knoten.push(el('em', null, kursiv));
    } else {
      knoten.push(ganz);
    }

    zuletzt = treffer.index + ganz.length;
  }

  if (zuletzt < text.length) knoten.push(text.slice(zuletzt));
  return fragment(...knoten);
}
